import React, { useState, useEffect } from 'react';

// ─── Référentiels de l'écran (mêmes clés que le serveur) ────────────────────
export const STATUTS = [
  { cle: 'prospect', libelle: 'Prospect' },
  { cle: 'discussion', libelle: 'En discussion' },
  { cle: 'client', libelle: 'Client' },
  { cle: 'perdu', libelle: 'Perdu' },
];
export const TYPES = [
  { cle: 'caviste', libelle: 'Caviste' },
  { cle: 'restaurateur', libelle: 'Restaurateur' },
  { cle: 'bio', libelle: 'Magasin bio' },
  { cle: 'distributeur', libelle: 'Distributeur' },
  { cle: 'autre', libelle: 'Autre' },
];
export const ECHANGES = [
  { cle: 'appel', libelle: 'Appel' },
  { cle: 'visite', libelle: 'Visite' },
  { cle: 'email', libelle: 'E-mail' },
  { cle: 'echantillons', libelle: 'Échantillons' },
  { cle: 'degustation', libelle: 'Dégustation' },
  { cle: 'commande', libelle: 'Commande' },
];
const trouver = (liste, cle) => liste.find((x) => x.cle === cle)?.libelle || cle || '';
export const libelleStatut = (c) => trouver(STATUTS, c);
export const libelleType = (c) => trouver(TYPES, c);
export const libelleEchange = (c) => trouver(ECHANGES, c);

// ─── Dates : le jour est celui de Paris, quel que soit le poste ─────────────
export const aujourdhui = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date());
const versDate = (iso) => new Date(`${iso}T12:00:00Z`);
const versIso = (d) => d.toISOString().slice(0, 10);
export const ajouterJours = (iso, n) => { const d = versDate(iso); d.setUTCDate(d.getUTCDate() + n); return versIso(d); };
export const ajouterMois = (iso, n) => { const d = versDate(iso); d.setUTCMonth(d.getUTCMonth() + n); return versIso(d); };
// Une relance qui tomberait un samedi ou un dimanche glisse au lundi.
export const jourOuvre = (iso) => {
  const j = versDate(iso).getUTCDay();
  return j === 6 ? ajouterJours(iso, 2) : j === 0 ? ajouterJours(iso, 1) : iso;
};
export const dateCourte = (iso) =>
  iso ? versDate(String(iso).slice(0, 10)).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';
export const dateJour = (iso) =>
  iso ? versDate(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';
export const dateLongue = (iso) =>
  versDate(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
// Un horodatage (échange) ramené au jour de Paris.
export const jourDe = (ts) => (ts ? new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date(ts)) : null);

// « Quand » d'une relance, à partir de l'écart en jours renvoyé par le serveur.
export function quandRelance(iso, dans) {
  if (!iso) return { texte: 'Aucune relance prévue', retard: false };
  const n = Number(dans);
  if (n < 0) return { texte: `Prévue le ${dateCourte(iso)} · ${-n} jour${n < -1 ? 's' : ''} de retard`, retard: true };
  if (n === 0) return { texte: "Aujourd'hui", retard: false };
  if (n === 1) return { texte: 'Demain', retard: false };
  return { texte: dateJour(iso), retard: false };
}

// ─── Navigation par l'adresse (#/comptes, #/compte/12…) ─────────────────────
// Le bouton « retour » du téléphone doit ramener à l'écran précédent : d'où
// l'adresse plutôt qu'un état React.
export function useRoute() {
  const lire = () => {
    const h = (window.location.hash || '#/relances').replace(/^#\/?/, '');
    const [chemin, requete = ''] = h.split('?');
    return { segments: chemin.split('/').filter(Boolean), params: new URLSearchParams(requete) };
  };
  const [route, setRoute] = useState(lire);
  useEffect(() => {
    const f = () => { setRoute(lire()); window.scrollTo(0, 0); };
    window.addEventListener('hashchange', f);
    return () => window.removeEventListener('hashchange', f);
  }, []);
  return route;
}
export const aller = (chemin) => { window.location.hash = chemin; };
export const remplacer = (chemin) => { window.location.replace(`${window.location.pathname}#${chemin.replace(/^#/, '')}`); };

export function useEstMobile() {
  const q = '(max-width: 760px)';
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mm = window.matchMedia(q);
    const f = () => setM(mm.matches);
    mm.addEventListener('change', f);
    return () => mm.removeEventListener('change', f);
  }, []);
  return m;
}

// ─── Petits composants ──────────────────────────────────────────────────────
export const Statut = ({ statut }) => <span className={`statut statut-${statut}`}>{libelleStatut(statut)}</span>;

// Bascule segmentée : le sélecteur maison (jamais une liste déroulante pour
// 2 à 5 choix).
export function Segmente({ options, valeur, onChange, etiquette, className = '' }) {
  return (
    <div className={`segmente ${className}`} role="group" aria-label={etiquette}>
      {options.map((o) => (
        <button key={o.cle} type="button" aria-pressed={valeur === o.cle}
          className={valeur === o.cle ? `actif ${o.classe || ''}` : ''} onClick={() => onChange(o.cle)}>
          {o.libelle}{o.n !== undefined && <span className="compteur">{o.n}</span>}
        </button>
      ))}
    </div>
  );
}

// Boutons-pilules (type d'échange, délai) : gros, tactiles.
export function Pilules({ options, valeur, onChange, etiquette, className = '' }) {
  return (
    <div className={`pilules ${className}`} role="group" aria-label={etiquette}>
      {options.map((o) => (
        <button key={o.cle} type="button" aria-pressed={valeur === o.cle}
          className={valeur === o.cle ? 'actif' : ''} onClick={() => onChange(o.cle)}>{o.libelle}</button>
      ))}
    </div>
  );
}

const svg = (d, extra) => (props) => (
  <svg width={props.taille || 18} height={props.taille || 18} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}{extra}</svg>
);
export const Icone = {
  Tel: svg(<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />),
  Mail: svg(<><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></>),
  Lieu: svg(<><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></>),
  Plus: svg(<path d="M12 5v14M5 12h14" />),
  Loupe: svg(<><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>),
  Retour: svg(<path d="m15 18-6-6 6-6" />),
  Crayon: svg(<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>),
  Coche: svg(<><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>),
  Liste: svg(<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />),
  Graphe: svg(<path d="M3 3v18h18M7 16v-5M12 16V8M17 16v-8" />),
  Import: svg(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m17 8-5-5-5 5M12 3v12" /></>),
  Fermer: svg(<path d="M18 6 6 18M6 6l12 12" />),
};

export const messageErreur = (e, defaut = 'Une erreur est survenue.') => e?.response?.data?.error || defaut;
export const prenom = (u) => String(u?.nom || u?.identifiant || '').split(/\s+/)[0].replace(/^./, (c) => c.toUpperCase());
